import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { MessageSquare, Plus } from "lucide-react";
import {
  api,
  type SupportTicket,
  type TicketStatus,
  type TicketType,
  type WorkshopTicket,
} from "../api";
import { fmtDateLong } from "../lib/format";
import { FeatureGate } from "../components/feature-gate";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";

function typeLabel(t: TFunction, type: TicketType) {
  if (type === "support") return t("type.support");
  if (type === "feedback") return t("type.feedback");
  return type;
}

function StatusBadge({ status }: { status: TicketStatus }) {
  const { t } = useTranslation("murojaatlar");
  if (status === "replied")
    return <Badge variant="success">{t("status.replied")}</Badge>;
  if (status === "seen")
    return <Badge variant="default">{t("status.seen")}</Badge>;
  return <Badge variant="secondary">{t("status.new")}</Badge>;
}

interface Props {
  onOpenTariff: () => void;
}

export function MurojaatlarPage({ onOpenTariff }: Props) {
  return (
    <FeatureGate
      feature="murojaatlar"
      requiredTariff="pro"
      onUpgrade={onOpenTariff}
    >
      <MurojaatlarInner />
    </FeatureGate>
  );
}

function MurojaatlarInner() {
  const { t } = useTranslation("murojaatlar");

  // Detail modal — ikkala bo'lim uchun umumiy (faqat o'qish).
  const [detail, setDetail] = useState<SupportTicket | null>(null);
  const [creating, setCreating] = useState(false);

  // Section 1: mening murojaatlarim
  const [mine, setMine] = useState<SupportTicket[] | null>(null);
  const [mineErr, setMineErr] = useState(false);
  const [mineReload, setMineReload] = useState(0);

  // Section 2: xodimlar murojaatlari
  const [team, setTeam] = useState<WorkshopTicket[] | null>(null);
  const [teamErr, setTeamErr] = useState(false);
  const [teamReload, setTeamReload] = useState(0);

  useEffect(() => {
    setMine(null);
    setMineErr(false);
    api
      .myTickets()
      .then(setMine)
      .catch(() => setMineErr(true));
  }, [mineReload]);

  useEffect(() => {
    setTeam(null);
    setTeamErr(false);
    api
      .workshopTickets()
      .then(setTeam)
      .catch(() => setTeamErr(true));
  }, [teamReload]);

  return (
    <div className="support-page space-y-6">
      {/* SECTION 1 — Mening murojaatlarim */}
      <Card className="support-card support-card--mine">
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{t("mine.title")}</CardTitle>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus />
            {t("mine.new")}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {mineErr ? (
            <RetryRow onRetry={() => setMineReload((k) => k + 1)} />
          ) : mine === null ? (
            <SkeletonRows />
          ) : mine.length === 0 ? (
            <EmptyState icon={<MessageSquare />} title={t("mine.empty")} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("table.type")}</TableHead>
                  <TableHead>{t("table.summary")}</TableHead>
                  <TableHead>{t("table.status")}</TableHead>
                  <TableHead>{t("table.date")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mine.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="cursor-pointer"
                    onClick={() => setDetail(ticket)}
                  >
                    <TableCell>{typeLabel(t, ticket.type)}</TableCell>
                    <TableCell className="font-medium">{ticket.title}</TableCell>
                    <TableCell>
                      <StatusBadge status={ticket.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {fmtDateLong(ticket.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* SECTION 2 — Xodimlar murojaatlari */}
      <Card className="support-card support-card--team">
        <CardHeader>
          <CardTitle>{t("team.title")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {teamErr ? (
            <RetryRow onRetry={() => setTeamReload((k) => k + 1)} />
          ) : team === null ? (
            <SkeletonRows />
          ) : team.length === 0 ? (
            <EmptyState
              icon={<MessageSquare />}
              title={t("team.empty")}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("table.employee")}</TableHead>
                  <TableHead>{t("table.type")}</TableHead>
                  <TableHead>{t("table.summary")}</TableHead>
                  <TableHead>{t("table.status")}</TableHead>
                  <TableHead>{t("table.date")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {team.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="cursor-pointer"
                    onClick={() => setDetail(ticket)}
                  >
                    <TableCell className="font-medium">
                      {ticket.sender.name}
                    </TableCell>
                    <TableCell>{typeLabel(t, ticket.type)}</TableCell>
                    <TableCell>{ticket.title}</TableCell>
                    <TableCell>
                      <StatusBadge status={ticket.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {fmtDateLong(ticket.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewTicketModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          setMineReload((k) => k + 1);
          toast.success(t("toast.sent"));
        }}
      />

      <TicketDetailModal ticket={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12" />
      ))}
    </div>
  );
}

function RetryRow({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("murojaatlar");
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="text-sm text-muted-foreground">{t("retry.error")}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t("retry.action")}
      </Button>
    </div>
  );
}

function NewTicketModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation("murojaatlar");

  const typeOptions: { value: TicketType; label: string }[] = [
    { value: "support", label: t("type.supportOption") },
    { value: "feedback", label: t("type.feedback") },
  ];

  const [type, setType] = useState<TicketType>("support");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal har ochilganda formani tozalaymiz.
  useEffect(() => {
    if (open) {
      setType("support");
      setTitle("");
      setBody("");
      setErr(null);
      setSubmitting(false);
    }
  }, [open]);

  const valid = !!title.trim() && !!body.trim();

  async function submit() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await api.createTicket({ type, title: title.trim(), body: body.trim() });
      onCreated();
    } catch (e) {
      setErr((e as { message?: string }).message || t("form.submitError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>{t("form.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody className="space-y-4">
        <div className="space-y-1.5">
          <Label>{t("form.type")}</Label>
          <div className="grid grid-cols-2 gap-2">
            {typeOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setType(o.value)}
                className={
                  "rounded-md border px-3 py-2 text-sm transition-colors " +
                  (type === o.value
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border text-muted-foreground hover:bg-accent")
                }
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ticket-title">{t("form.summary")}</Label>
          <Input
            id="ticket-title"
            value={title}
            maxLength={100}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("form.summaryPlaceholder")}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ticket-body">{t("form.body")}</Label>
          <Textarea
            id="ticket-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("form.bodyPlaceholder")}
            className="min-h-[120px]"
          />
        </div>

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
            {err}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          {t("form.cancel")}
        </Button>
        <Button onClick={submit} disabled={!valid || submitting}>
          {submitting ? t("form.submitting") : t("form.submit")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function TicketDetailModal({
  ticket,
  onClose,
}: {
  ticket: SupportTicket | null;
  onClose: () => void;
}) {
  const { t } = useTranslation("murojaatlar");

  return (
    <Dialog open={!!ticket} onOpenChange={(o) => !o && onClose()}>
      {ticket && (
        <>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{typeLabel(t, ticket.type)}</Badge>
              <span className="text-sm text-muted-foreground">
                {fmtDateLong(ticket.created_at)}
              </span>
            </div>
            <DialogTitle className="mt-2">{ticket.title}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="text-sm whitespace-pre-wrap text-foreground">
              {ticket.body}
            </p>

            {ticket.status === "replied" ? (
              <div className="rounded-md border border-success/30 bg-success/5 p-4 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-success">
                    {t("detail.reply")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {fmtDateLong(ticket.replied_at)}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap text-foreground">
                  {ticket.admin_reply}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("detail.waiting")}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {t("detail.close")}
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
